// ไฟล์: frontend/src/pages/CreateNewsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

const CreateNewsPage = ({ user }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category_id: '',
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // ดึงข้อมูลหมวดหมู่
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get('/categories');
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถดึงข้อมูลหมวดหมู่ได้',
        });
      }
    };

    fetchCategories();
  }, []);

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // จัดการการอัปโหลดรูปภาพ
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnail(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // ตรวจสอบข้อมูลก่อนส่ง
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'กรุณาระบุหัวข้อข่าวสาร';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'กรุณาระบุเนื้อหาข่าวสาร';
    }
    
    if (!formData.category_id) {
      newErrors.category_id = 'กรุณาเลือกหมวดหมู่';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // จัดการการส่งฟอร์ม
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('category_id', formData.category_id);
      
      if (thumbnail) {
        formDataToSend.append('thumbnail', thumbnail);
      }
      
      await axios.post('/news', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ!',
        text: 'สร้างข่าวสารใหม่เรียบร้อยแล้ว',
        confirmButtonText: 'ตกลง',
      }).then(() => {
        navigate('/news');
      });
    } catch (error) {
      console.error('Error creating news:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถสร้างข่าวสารได้',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-news-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-newspaper"></i> สร้างข่าวสารใหม่
          </h1>
          <p className="page-subtitle">เพิ่มข่าวสารเพื่อแจ้งให้ครูทราบเกี่ยวกับกิจกรรม อบรม หรือข่าวสารที่น่าสนใจ</p>
        </div>

        <div className="card">
          <div className="card-header">
            <i className="fas fa-edit"></i> แบบฟอร์มสร้างข่าวสาร
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title" className="form-label">หัวข้อข่าวสาร <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="ระบุหัวข้อข่าวสาร"
                />
                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="category_id" className="form-label">หมวดหมู่ <span className="text-danger">*</span></label>
                <select
                  className={`form-select ${errors.category_id ? 'is-invalid' : ''}`}
                  id="category_id"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                >
                  <option value="">-- เลือกหมวดหมู่ --</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="thumbnail" className="form-label">รูปภาพประกอบ</label>
                <input
                  type="file"
                  className="form-control"
                  id="thumbnail"
                  name="thumbnail"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                />
                <small className="text-muted">แนะนำขนาด 1200 x 630 พิกเซล (16:9)</small>
                
                {thumbnailPreview && (
                  <div className="thumbnail-preview mt-3">
                    <img src={thumbnailPreview} alt="Thumbnail Preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="content" className="form-label">เนื้อหาข่าวสาร <span className="text-danger">*</span></label>
                <textarea
                  className={`form-control ${errors.content ? 'is-invalid' : ''}`}
                  id="content"
                  name="content"
                  rows="10"
                  value={formData.content}
                  onChange={handleChange}
                  placeholder="เขียนเนื้อหาข่าวสารที่นี่..."
                ></textarea>
                {errors.content && <div className="invalid-feedback">{errors.content}</div>}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-light" onClick={() => navigate('/news')}>
                  <i className="fas fa-arrow-left"></i> ยกเลิก
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> กำลังบันทึก...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> บันทึกข่าวสาร
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .page-header {
          margin-bottom: 2rem;
        }
        
        .page-subtitle {
          color: var(--gray-600);
        }
        
        .thumbnail-preview {
          width: 100%;
          max-width: 400px;
          border-radius: var(--border-radius-md);
          overflow: hidden;
          box-shadow: var(--shadow-sm);
        }
        
        .thumbnail-preview img {
          width: 100%;
          height: auto;
        }
        
        .form-actions {
          display: flex;
          justify-content: space-between;
          margin-top: 2rem;
        }
      `}</style>
    </div>
  );
};

export default CreateNewsPage;